// Minimal DOM helpers. Deliberately not a framework (§2.2).

export function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);

  // A <button> with no type attribute defaults to type="submit", which inside a
  // <form> submits it — and outside one does nothing useful. That default has
  // already caused one silent-no-op bug here, so buttons are explicitly
  // type="button" unless a caller asks for a submit.
  if (tag === 'button' && !('type' in props)) node.type = 'button';

  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v);
    } else if (k === 'dataset') Object.assign(node.dataset, v);
    else node.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

export const clear = (node) => { while (node.firstChild) node.removeChild(node.firstChild); };

export function mount(node, ...children) {
  clear(node);
  node.append(...children.flat().filter(Boolean));
  return node;
}

// Dates arrive as 'YYYY-MM-DD' (a Yangon calendar date, §2.5). Parsing that
// with `new Date()` would apply the browser's timezone and can shift the day,
// so the parts are split manually.
export function formatDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[m - 1]} ${y}`;
}

// Day name helps staff read a due date out loud: "back on Sunday the 16th".
export function formatDateLong(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const dow = days[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${dow}, ${formatDate(ymd)}`;
}

// The logo is a school asset that may legitimately be missing (a fresh
// white-label clone before the file is dropped in). A broken-image icon in the
// header looks like a fault; hiding it degrades to a clean text-only header.
export function logoImg(src, className, alt = '') {
  return el('img', {
    src, alt, class: className,
    onError: (e) => { e.target.style.display = 'none'; },
  });
}
